import { trackEvent } from "@/lib/analytics";

/**
 * Export data to CSV format
 */
export function exportCsv<T extends Record<string, unknown>>(
  data: T[],
  columns: { key: keyof T; label: string }[],
  filename: string
): void {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Create CSV header
  const header = columns.map(col => `"${col.label}"`).join(',');

  // Create CSV rows
  const rows = data.map(row => 
    columns.map(col => {
      const value = row[col.key];
      
      // Handle different data types
      if (value === null || value === undefined) {
        return '""';
      }
      
      if (typeof value === 'number') {
        return value.toString();
      }
      
      if (typeof value === 'object' && value !== null && 'toISOString' in value) {
        return `"${(value as Date).toISOString().split('T')[0]}"`;
      }
      
      // Escape quotes in strings
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(',')
  );

  // Combine header and rows
  const csv = [header, ...rows].join('\n');

  trackEvent('export_csv', { row_count: data.length });

  // Create and trigger download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export data to Excel-compatible CSV format
 * Ensures proper formatting for Excel to recognize dates and numbers
 */
export function exportXlsx<T extends Record<string, unknown>>(
  data: T[],
  columns: { key: keyof T; label: string; type?: 'string' | 'number' | 'date' }[],
  filename: string
): void {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Create CSV header
  const header = columns.map(col => `"${col.label}"`).join(',');

  // Create CSV rows with type formatting
  const rows = data.map(row => 
    columns.map(col => {
      const value = row[col.key];
      const type = col.type || 'string';
      
      // Handle null/undefined
      if (value === null || value === undefined) {
        return '""';
      }
      
      // Format based on type
      switch (type) {
        case 'number':
          // Return raw number for Excel to recognize
          return typeof value === 'number' ? value.toString() : parseFloat(value).toString();
        
        case 'date': {
          // Format as ISO date for Excel
          if (typeof value === 'object' && value !== null && 'toISOString' in value) {
            return `"${(value as Date).toISOString().split('T')[0]}"`;
          }
          const dateValue = new Date(String(value));
          return `"${dateValue.toISOString().split('T')[0]}"`;
        }

        case 'string':
        default:
          // Escape quotes in strings
          return `"${String(value).replace(/"/g, '""')}"`;
      }
    }).join(',')
  );

  // Combine with BOM for Excel UTF-8 recognition
  const csv = '\ufeff' + [header, ...rows].join('\n');

  // Create and trigger download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Format currency for export
 */
export function formatCurrency(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Format date for export
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
}
